import { io } from 'socket.io-client';
import jwtService from './jwt.service';

class SocketService {
    constructor() {
        this.socket = null;
        this.connected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.listeners = new Map();
    }

    // Get socket server URL based on environment
    getSocketUrl() {
        const hostname = location.hostname;

        if (hostname === 'manager.mlshipafrica.app') {
            return 'https://api.mlshipafrica.app';
        } else if (hostname === 'rmexpress.storeino.world') {
            return 'https://rmexpress.storeino.world';
        } else if (hostname === 'rmexpress.speedliv.com') {
            return 'https://api-rmexpress.speedliv.com';
        } else {
            return 'http://localhost:3021';
        }
    }

    // Connect to socket server
    connect(store) {
        // If socket exists and is connected, return it
        if (this.socket && this.socket.connected) {
            console.log('[Socket] Already connected');
            return this.socket;
        }

        // If socket exists but disconnected, try to reconnect
        if (this.socket && !this.socket.connected) {
            console.log('[Socket] Socket exists but disconnected, reconnecting...');
            this.socket.connect();
            return this.socket;
        }

        const token = jwtService.getToken();
        if (!token) {
            console.log('[Socket] No token available for socket connection');
            return null;
        }

        const url = this.getSocketUrl();
        console.log('[Socket] Connecting to socket server:', url);

        this.socket = io(url, {
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: this.maxReconnectAttempts,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000
        });

        // Connection events
        this.socket.on('connect', () => {
            console.log('[Socket] Connected:', this.socket.id);
            this.connected = true;
            this.reconnectAttempts = 0;

            // Re-attach any pending listeners that were registered before socket was ready
            this.reattachListeners();

            if (store) {
                store.commit('chat/SET_CONNECTED', true);
            }
        });

        this.socket.on('disconnect', (reason) => {
            console.log('[Socket] Disconnected:', reason);
            this.connected = false;

            if (store) {
                store.commit('chat/SET_CONNECTED', false);
            }
        });

        this.socket.on('reconnect', (attemptNumber) => {
            console.log('[Socket] Reconnected after', attemptNumber, 'attempts');
            this.connected = true;

            // Re-attach listeners after reconnection
            this.reattachListeners();

            if (store) {
                store.commit('chat/SET_CONNECTED', true);
            }
        });

        this.socket.on('connect_error', (error) => {
            console.error('[Socket] Connection error:', error.message);
            this.reconnectAttempts++;

            if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                console.log('[Socket] Max reconnection attempts reached');
            }
        });

        this.socket.on('error', (error) => {
            console.error('[Socket] Error:', error);
        });

        // Store the socket instance in store
        if (store) {
            store.commit('chat/SET_SOCKET', this.socket);
        }

        return this.socket;
    }

    // Disconnect from socket server
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.connected = false;
        }
    }

    // Emit event
    emit(event, data) {
        // Use socket.connected (actual socket.io state) instead of our flag
        if (this.socket && this.socket.connected) {
            console.log('[Socket] Emitting:', event, data);
            this.socket.emit(event, data);
        } else {
            console.warn('[Socket] Not connected, cannot emit:', event, 'socket:', !!this.socket, 'connected:', this.socket ? this.socket.connected : false);
        }
    }

    // Listen for event
    on(event, callback) {
        // Store listener for cleanup and potential re-registration
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);

        if (this.socket) {
            this.socket.on(event, callback);
            console.log('[Socket] Registered listener for:', event);
        } else {
            console.warn('[Socket] Socket not ready, listener stored for:', event);
        }
    }

    // Re-attach all stored listeners to the socket (called on connect/reconnect)
    reattachListeners() {
        if (!this.socket) return;

        console.log('[Socket] Re-attaching', this.listeners.size, 'event types');
        this.listeners.forEach((callbacks, event) => {
            callbacks.forEach(callback => {
                // Remove first to avoid duplicates
                this.socket.off(event, callback);
                this.socket.on(event, callback);
            });
            console.log('[Socket] Re-attached', callbacks.length, 'listeners for:', event);
        });
    }

    // Remove listener
    off(event, callback) {
        if (this.socket) {
            if (callback) {
                this.socket.off(event, callback);
            } else {
                this.socket.off(event);
            }
        }
    }

    // Remove all listeners for an event
    removeAllListeners(event) {
        if (this.socket) {
            this.socket.removeAllListeners(event);
            this.listeners.delete(event);
        }
    }

    // Check if connected
    isConnected() {
        return this.connected && this.socket && this.socket.connected;
    }

    // Get socket instance
    getSocket() {
        return this.socket;
    }

    // Join a conversation room
    joinConversation(conversationId) {
        this.emit('join_conversation', { conversationId });
    }

    // Leave a conversation room
    leaveConversation(conversationId) {
        this.emit('leave_conversation', { conversationId });
    }

    // Send a message - returns true if emitted, false if socket not connected
    sendMessage(conversationId, content, type = 'text', attachment = null, tempId = null) {
        console.log('[Socket] sendMessage called, socket exists:', !!this.socket, 'connected:', this.socket ? this.socket.connected : false);
        if (this.socket && this.socket.connected) {
            this.socket.emit('send_message', {
                conversationId,
                content,
                type,
                attachment,
                tempId
            });
            return true;
        }
        console.warn('[Socket] Cannot send message - socket not connected');
        return false;
    }

    // Start typing indicator
    startTyping(conversationId) {
        this.emit('typing_start', { conversationId });
    }

    // Stop typing indicator
    stopTyping(conversationId) {
        this.emit('typing_stop', { conversationId });
    }

    // Mark messages as read
    markAsRead(conversationId) {
        this.emit('mark_read', { conversationId });
    }

    // Get online status
    getOnlineStatus(userIds) {
        this.emit('get_online_status', { userIds });
    }

    // Heartbeat
    sendHeartbeat() {
        this.emit('heartbeat');
    }
}

// Create singleton instance
const socketService = new SocketService();

// Vue plugin
export default {
    install(Vue) {
        Vue.prototype.$socket = socketService;
    }
};

export { socketService };
